import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './client';

// ===== Types =====

/** MKS-327 — is the person working this day, just elsewhere? */
export type TimeOffCategory = 'absence' | 'presence';

/** MKS-327 — what, if anything, a request of this type draws down. */
export type TimeOffAllowanceKind = 'none' | 'vacation_entitlement' | 'own_yearly_days';

/**
 * MKS-327 — a time-off type and the behaviour an admin configured for it.
 *
 * The behaviour fields are optional on the TS type and the SPA falls back to
 * today's behaviour when they are missing, because an old cached response (or
 * a server where the MKS-327 flag is off) can still deliver a bare row.
 */
export interface TimeOffType {
  id: number;
  name: string;
  /**
   * Stable identifier. Until MKS-327 this was how the SPA told which type
   * draws the vacation balance; use `allowanceKind` for that now and keep this
   * for display/debugging only. Never match on the (Czech) `name`.
   */
  code?: string | null;
  category?: TimeOffCategory;
  isPaid?: boolean;
  countsTowardHoursTarget?: boolean;
  allowanceKind?: TimeOffAllowanceKind;
  allowanceDays?: number | null;
  needsApproval?: boolean;
  partialDayAllowed?: boolean;
  /** Hex colour for the team calendar and the attendance report. */
  colour?: string;
  setsSlackStatus?: boolean;
  slackStatusEmoji?: string | null;
  /** Archived types stay on historical records but cannot be picked. */
  archived?: boolean;
}

/**
 * Does this type draw the vacation balance?
 *
 * MKS-327 moved the decision off the `code` literal and onto the type's own
 * `allowanceKind`, so an admin says which type draws the entitlement.
 */
export function drawsVacationBalance(type: TimeOffType | undefined): boolean {
  if (!type) return false;
  return type.allowanceKind === 'vacation_entitlement';
}

/** MKS-327 — one type's yearly cap in days and what is left of it this year. */
export interface TimeOffAllowance {
  timeOffTypeId: number;
  typeName: string | null;
  year: number;
  allowanceDays: number;
  usedDays: number;
  pendingDays: number;
  remainingDays: number;
}

/** MKS-327 — the attributes the admin page sends when creating or editing a type. */
export interface TimeOffTypeInput {
  name?: string;
  code?: string | null;
  category?: TimeOffCategory;
  isPaid?: boolean;
  countsTowardHoursTarget?: boolean;
  allowanceKind?: TimeOffAllowanceKind;
  allowanceDays?: number | null;
  needsApproval?: boolean;
  partialDayAllowed?: boolean;
  colour?: string;
  setsSlackStatus?: boolean;
  slackStatusEmoji?: string | null;
  archived?: boolean;
}

/**
 * MKS-326 — the signed-in user's vacation balance.
 *
 * Every field is `null` for someone with no employee (HPP) relationship this
 * year — contractors and freelancers have no entitlement, so the UI shows them
 * nothing and checks nothing.
 *
 * `dailyWorkingHours` is what ONE whole working day is worth for this person
 * (8 × FTE). It is the conversion factor between the hour figures here and the
 * notional 8 h day that `TimeOff.totalHours` / `dateFromHours` are stored in.
 */
export interface VacationBalance {
  year: number;
  entitlementHours: number | null;
  usedHours: number | null;
  pendingHours: number | null;
  carriedInHours: number | null;
  remainingHours: number | null;
  dailyWorkingHours: number | null;
}

export interface TimeOffDay {
  id: number;
  date: string | null;
  hours: number;
}

export interface TimeOff {
  id: number;
  userId: number;
  user: { id: number; email: string; firstName: string | null; lastName: string | null } | null;
  // MKS-327: these ride along on every request row so the calendar can colour
  // the span and mark a presence day as working, and the /time grid can tell
  // whether the day fills the person's hours target.
  timeOffType: {
    id: number;
    name: string | null;
    category?: TimeOffCategory;
    colour?: string;
    countsTowardHoursTarget?: boolean;
  } | null;
  dateFrom: string | null;
  dateTo: string | null;
  dateFromHours: number;
  dateToHours: number;
  state: string;
  totalDays: number;
  totalHours: number;
  days: TimeOffDay[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateTimeOffInput {
  timeOffTypeId: number;
  dateFrom: string;
  dateTo: string;
  dateFromHours?: number;
  dateToHours?: number;
  // MKS-361 follow-up: no `substitutes` here. The requester is told which
  // projects their absence affects, but who covers them is the project
  // manager's call, made through PUT /time-off/:id/substitutes.
}

export interface UpdateTimeOffInput {
  timeOffTypeId?: number;
  dateFrom?: string;
  dateTo?: string;
  dateFromHours?: number;
  dateToHours?: number;
}

export interface TimeOffSummary {
  totalDaysTaken: number;
  totalHoursTaken: number;
  byType: { typeId: number; typeName: string; days: number; hours: number }[];
}

// ===== API Functions =====

/**
 * `includeArchived` is for the admin page only — every picker wants the
 * pickable set, which is what the backend returns by default.
 */
export async function getTimeOffTypes(options?: {
  includeArchived?: boolean;
}): Promise<TimeOffType[]> {
  const query = options?.includeArchived ? '?includeArchived=true' : '';
  const res = await apiGet<{ data: TimeOffType[] }>(`/time-off/types${query}`);
  return res.data;
}

export async function createTimeOffType(input: TimeOffTypeInput): Promise<TimeOffType> {
  const res = await apiPost<{ data: TimeOffType }>('/time-off/types', input);
  return res.data;
}

export async function updateTimeOffType(
  id: number,
  input: TimeOffTypeInput
): Promise<TimeOffType> {
  const res = await apiPut<{ data: TimeOffType }>(`/time-off/types/${id}`, input);
  return res.data;
}

/** MKS-327 — the signed-in user's remaining yearly allowance per capped type. */
export async function getMyTimeOffAllowances(year?: number): Promise<TimeOffAllowance[]> {
  const query = year ? `?year=${year}` : '';
  const res = await apiGet<{ data: TimeOffAllowance[] }>(`/time-off/my-allowances${query}`);
  return res.data;
}

export async function deleteTimeOffType(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/time-off/types/${id}`);
}

export async function getUserTimeOffs(filters?: { year?: number; state?: string }): Promise<TimeOff[]> {
  const params = new URLSearchParams();
  if (filters?.year) params.set('year', String(filters.year));
  if (filters?.state) params.set('state', filters.state);
  const query = params.toString();
  const res = await apiGet<{ data: TimeOff[] }>(`/time-off${query ? `?${query}` : ''}`);
  return res.data;
}

export async function getMyVacationBalance(year?: number): Promise<VacationBalance> {
  const query = year ? `?year=${year}` : '';
  const res = await apiGet<{ data: VacationBalance }>(`/time-off/my-vacation-balance${query}`);
  return res.data;
}

export async function getTimeOff(id: number): Promise<TimeOff> {
  const res = await apiGet<{ data: TimeOff }>(`/time-off/${id}`);
  return res.data;
}

export async function createTimeOffRequest(input: CreateTimeOffInput): Promise<TimeOff> {
  const res = await apiPost<{ data: TimeOff }>('/time-off', input);
  return res.data;
}

export async function updateTimeOffRequest(id: number, input: UpdateTimeOffInput): Promise<TimeOff> {
  const res = await apiPatch<{ data: TimeOff }>(`/time-off/${id}`, input);
  return res.data;
}

export async function submitTimeOff(id: number): Promise<TimeOff> {
  const res = await apiPost<{ data: TimeOff }>(`/time-off/${id}/submit`);
  return res.data;
}

export async function cancelTimeOff(id: number): Promise<TimeOff> {
  const res = await apiDelete<{ data: TimeOff }>(`/time-off/${id}`);
  return res.data;
}

export async function adminCancelTimeOff(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/time-off/admin/${id}`);
}

export async function approveTimeOff(id: number): Promise<TimeOff> {
  const res = await apiPost<{ data: TimeOff }>(`/time-off/${id}/approve`);
  return res.data;
}

export async function rejectTimeOff(id: number, reason?: string): Promise<TimeOff> {
  const res = await apiPost<{ data: TimeOff }>(`/time-off/${id}/reject`, reason ? { reason } : undefined);
  return res.data;
}

export async function getTimeOffSummary(userId?: number, year?: number): Promise<TimeOffSummary> {
  const params = new URLSearchParams();
  if (userId) params.set('userId', String(userId));
  if (year) params.set('year', String(year));
  const query = params.toString();
  const res = await apiGet<{ data: TimeOffSummary }>(`/time-off/summary${query ? `?${query}` : ''}`);
  return res.data;
}

// ===== Team Functions =====

export interface TeamTimeOff {
  id: number;
  userId: number;
  userName: string;
  dateFrom: string | null;
  dateTo: string | null;
}

export async function getTeamTimeOffs(): Promise<TeamTimeOff[]> {
  const res = await apiGet<{ data: TeamTimeOff[] }>('/time-off/team');
  return res.data;
}

// ===== Admin Functions =====

export async function getPendingTimeOffs(): Promise<TimeOff[]> {
  const res = await apiGet<{ data: TimeOff[] }>('/time-off/admin/pending');
  return res.data;
}

export async function getAllTimeOffs(filters?: { year?: number; state?: string }): Promise<TimeOff[]> {
  const params = new URLSearchParams();
  if (filters?.year) params.set('year', String(filters.year));
  if (filters?.state) params.set('state', filters.state);
  const query = params.toString();
  const res = await apiGet<{ data: TimeOff[] }>(`/time-off/admin/all${query ? `?${query}` : ''}`);
  return res.data;
}

// ===== Bulk Operations =====

export interface BulkOperationResult {
  approved?: number[];
  rejected?: number[];
  cancelled?: number[];
  failed: { id: number; error: string }[];
}

export async function bulkApproveTimeOffs(ids: number[]): Promise<BulkOperationResult> {
  const res = await apiPost<{ data: BulkOperationResult }>('/time-off/bulk/approve', { ids });
  return res.data;
}

export async function bulkRejectTimeOffs(ids: number[], reason?: string): Promise<BulkOperationResult> {
  const res = await apiPost<{ data: BulkOperationResult }>('/time-off/bulk/reject', { ids, reason });
  return res.data;
}

export async function bulkSubmitTimeOffs(ids: number[]): Promise<BulkOperationResult> {
  const res = await apiPost<{ data: BulkOperationResult }>('/time-off/bulk/submit', { ids });
  return res.data;
}

export async function bulkCancelTimeOffs(ids: number[]): Promise<BulkOperationResult> {
  const res = await apiPost<{ data: BulkOperationResult }>('/time-off/bulk/cancel', { ids });
  return res.data;
}
